import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Activity, MessageCircle, Trophy, ShoppingBag, Watch, Users, LogOut } from "lucide-react";

const links = [
  { to: "/app", label: "TODAY", end: true, icon: Activity, testId: "nav-today" },
  { to: "/app/coach", label: "FLYN", icon: MessageCircle, testId: "nav-coach" },
  { to: "/app/challenges", label: "STAKES", icon: Trophy, testId: "nav-challenges" },
  { to: "/app/shop", label: "STACK", icon: ShoppingBag, testId: "nav-shop" },
  { to: "/app/wearables", label: "CONNECT", icon: Watch, testId: "nav-wearables" },
  { to: "/app/squad", label: "SQUAD", icon: Users, testId: "nav-squad" },
];

export default function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-white">
      {/* Desktop top nav */}
      <header className="hidden md:block sticky top-0 z-30 backdrop-blur-md bg-white/80 border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-10">
            <NavLink to="/app" className="font-display text-2xl tracking-tight" data-testid="app-logo">
              FS LAB
            </NavLink>
            <nav className="flex items-center gap-7">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.end}
                  data-testid={l.testId}
                  className={({ isActive }) =>
                    `font-mono text-xs uppercase tracking-[0.2em] transition-colors ${
                      isActive ? "text-black" : "text-neutral-500 hover:text-black"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
            </nav>
          </div>
          <div className="flex items-center gap-4">
            <div className="hidden md:flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-neutral-500">
              {user?.connected_wearable === "whoop" ? (
                <span data-testid="whoop-sync-status" className="lime-dot">WHOOP SYNCED</span>
              ) : (
                <span data-testid="whoop-sync-status" className="text-neutral-400">NO WEARABLE</span>
              )}
            </div>
            <div className="w-9 h-9 rounded-full bg-obsidian text-white flex items-center justify-center font-mono text-xs" data-testid="user-avatar">
              {user?.avatar_initials || "?"}
            </div>
            <button
              data-testid="logout-btn"
              onClick={async () => { await logout(); navigate("/"); }}
              className="text-neutral-500 hover:text-black transition-colors"
              title="Log out"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="pb-28 md:pb-12">
        <Outlet />
      </main>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-30 backdrop-blur-md bg-white/95 border-t border-neutral-200 pb-safe">
        <div className="grid grid-cols-6 px-2 py-2">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={`mobile-${l.testId}`}
              className={({ isActive }) =>
                `flex flex-col items-center gap-1 py-2 transition-colors ${
                  isActive ? "text-black" : "text-neutral-400"
                }`
              }
            >
              <l.icon className="w-5 h-5" />
              <span className="font-mono text-[9px] tracking-widest">{l.label}</span>
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
